import { useState } from "react";
import RegistrationStepLayout from "./RegistrationStepLayout";
import { uploadImages } from "../../asiosConfig";

import { PROFILE_UI_CONTENT } from "./profileContextContent";
import { resolveProfileUIText } from "./resolveProfileUIText";

// ICONS
import photoIcon from "../../images/photoUploadIcon.png";
import photoUploadActive from "../../images/photoUploadActive.png";
import photoUploadGray from "../../images/photoUploadGray.png";

import infoIcon from "../../images/i-buton.png";

import crossImage from "../../images/Crossimage.png";

/* =====================================================
   HELPERS
===================================================== */

// reused from old RegisterPhotoStep
const resizeImage = (file, maxWidth = 800, maxHeight = 800) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((maxWidth / width) * height);
            width = maxWidth;
          } else {
            width = Math.round((maxHeight / height) * width);
            height = maxHeight;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            resolve(
              new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
                type: "image/jpeg",
                lastModified: Date.now(),
              }),
            );
          },
          "image/jpeg",
          0.7,
        );
      };
    };
  });
};

/* =====================================================
   PHOTO UPLOAD FLOW
===================================================== */

const PhotoUploadFlow = ({ onCompleted }) => {
  return <PhotoUploadStep onCompleted={onCompleted} />;
};

export default PhotoUploadFlow;

/* =====================================================
   STEP – PHOTO UPLOAD
===================================================== */

const PhotoUploadStep = ({ onCompleted }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);

  const [uploadError, setUploadError] = useState("");

  const profileCreatedById = Number(localStorage.getItem("profileCreatedById"));
  const gender = localStorage.getItem("selectedGender");

  const resolved = resolveProfileUIText({
    profileCreatedById,
    gender,
    stepKey: "PHOTO_UPLOAD",
    stepConfig: PROFILE_UI_CONTENT.PHOTO_UPLOAD,
  });

  const [photos, setPhotos] = useState(
    Array(6).fill({ file: null, preview: null }),
  );
  const [error, setError] = useState("");

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const activeIndex = photos.findIndex((p) => !p.file);

  /* ---------------- UPLOAD ---------------- */

  const handleImageChange = (file) => {
    if (!file) return;

    // validation (inline, no Formik/Yup)
    if (file.size > 10 * 1024 * 1024) {
      setError("File must be less than or equal to 10MB");
      return;
    }

    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
        file.type,
      )
    ) {
      setError("Unsupported file format");
      return;
    }

    setError("");

    setPhotos((prev) => {
      const next = [...prev];
      if (activeIndex !== -1) {
        next[activeIndex] = {
          file,
          preview: URL.createObjectURL(file),
        };
      }
      return next;
    });
  };

  /* ---------------- REMOVE ---------------- */

  const removeImage = (index) => {
    setPhotos((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      next.push({ file: null, preview: null });
      return next;
    });
  };

  /* ---------------- SET PROFILE PHOTO ---------------- */

  const setAsProfilePhoto = (index) => {
    setPhotos((prev) => {
      const next = [...prev];
      const temp = next[0];
      next[0] = next[index];
      next[index] = temp;
      return next;
    });
  };

  const handleContinue = async () => {
    const validPhotos = photos.filter((p) => p.file);

    if (!validPhotos.length) {
      onCompleted();
      return;
    }

    setUploading(true);
    setUploadDone(false);
    setUploadError("");

    try {
      const formData = new FormData();

      for (const p of validPhotos) {
        const compressedFile = await resizeImage(p.file);
        formData.append("photos[]", compressedFile);
      }

      formData.append("user_id", userId);
      formData.append("token", token);

      await uploadImages(formData);

      setUploadDone(true);

      setTimeout(() => {
        onCompleted();
      }, 800);
    } catch (error) {
      setUploadError(
        error?.response?.data?.message ||
          "Photo upload failed. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <RegistrationStepLayout
        icon={photoIcon}
        // title="Upload Photos"
        // subtitle="Profiles with photos get more responses. Upload at least one photo."

        title={resolved.title}
        subtitle={resolved.subtitle}
        stepText="Step 1 of 1"
        onContinue={handleContinue}
        continueVariant="button"
        continueDisabled={uploading}
        rightHeaderContent={
          <div
            onClick={onCompleted}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "rgba(230,0,0,0.81)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Skip
          </div>
        }
      >
        <PhotoGrid
          photos={photos}
          activeIndex={activeIndex}
          onUpload={handleImageChange}
          onRemove={removeImage}
          onSetProfile={setAsProfilePhoto}
        />

        {error && (
          <div style={{ marginTop: 12, color: "#ff3c3c", fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* SKIP */}
        {/* <div
          onClick={onCompleted}
          style={{
            marginTop: 18,
            width: "100%",
            height: 50,
            borderRadius: 50,
            border: "1px solid transparent",
            background:
              "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81)) border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            color: "rgba(230,0,0,0.81)",
            cursor: "pointer",
          }}
        >
          Skip for now
        </div> */}

        <div
          style={{
            marginTop: 15,
            width: "100%",
            padding: "8px 15px",
            border: "1px dashed rgba(230,0,0,0.5)",
            borderRadius: 12,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            background: "#fff",
          }}
        >
          <img
            src={infoIcon}
            alt="info"
            style={{
              width: 22,
              height: 22,
              flexShrink: 0,
              marginTop: 2,
            }}
          />

          <span
            style={{
              fontSize: 13,
              color: "#555",
              fontFamily: "Montserrat",
              lineHeight: "1.8",
              textAlign: "left",
            }}
          >
            You can upload a maximum of 6 photos during registration. You can
            add more photos later from edit profile.
          </span>
        </div>

        {uploadError && (
          <div
            style={{
              marginTop: 12,
              color: "#ff3c3c",
              fontSize: 13,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {uploadError}
          </div>
        )}

        {uploading && (
          <div
            style={{
              position: "absolute", // 🔑 NOT fixed
              inset: 0,
              background: "rgba(255,255,255,0.85)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
              fontFamily: "Montserrat",
              borderRadius: 16,
            }}
          >
            <div className="spinner" />
            <div style={{ fontWeight: 600 }}>Uploading photos…</div>
            <div style={{ fontSize: 14, color: "#504f4f" }}>
              Please don’t close this page
            </div>
          </div>
        )}

        {uploadDone && !uploading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.85)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              fontFamily: "Montserrat",
              color: "#28a745",
              borderRadius: 16,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "#28a745",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 32,
                fontWeight: 700,
              }}
            >
              ✓
            </div>
            <div style={{ fontWeight: 600 }}>Photos uploaded successfully</div>
          </div>
        )}
      </RegistrationStepLayout>
    </>
  );
};

/* =====================================================
   PHOTO GRID
===================================================== */

const PhotoGrid = ({
  photos,
  index,
  photo,
  activeIndex,
  onUpload,
  onRemove,
  onSetProfile,
}) => {
  const Container = ({ index, photo }) => {
    const isActive = index === activeIndex;

    const size =
      index === 0 ? { width: 300, height: 240 } : { width: 140, height: 110 };

    return (
      <div
        style={{
          ...size,
          border: "2px dashed rgba(230,0,0,0.81)",
          borderRadius: 16,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {photo.file ? (
          <>
            {index === 0 && photo.file && (
              <div
                style={{
                  position: "absolute",
                  top: 12,
                  left: 0,
                  height: 25,
                  padding: "0 14px",
                  display: "flex",
                  alignItems: "center",

                  borderRadius: "0px 50px 50px 0px",
                  background:
                    "linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), linear-gradient(90deg, #ff7705, #e60000)",

                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  zIndex: 3,
                  whiteSpace: "nowrap",
                }}
              >
                Profile Photo
              </div>
            )}

            <img
              src={photo.preview}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />

            <button onClick={() => onRemove(index)} style={removeBtn}>
              <img
                src={crossImage}
                alt="remove"
                style={{
                  width: 25,
                  height: 25,
                }}
              />
            </button>

            {index !== 0 && (
              <button onClick={() => onSetProfile(index)} style={profileBtn}>
                Set as Profile Photo
              </button>
            )}
          </>
        ) : (
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              cursor: isActive ? "pointer" : "default",
            }}
          >
            <img
              src={isActive ? photoUploadActive : photoUploadGray}
              alt="upload"
              style={{
                width: index === 0 ? 42 : 28, // 👈 smaller icon
                height: index === 0 ? 42 : 28,
                opacity: isActive ? 1 : 0.6,
              }}
            />

            <span
              style={{
                fontSize: 12,
                color: "#888",
                fontWeight: 500,
              }}
            >
              Upload Photo
            </span>

            {isActive && (
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => onUpload(e.target.files[0])}
              />
            )}
          </label>
        )}
      </div>
    );
  };

  return (
    <div
      style={{ marginTop: 20, display: "grid", gap: 12, alignSelf: "center" }}
    >
      <div style={{ display: "flex", gap: 12 }}>
        <Container index={0} photo={photos[0]} />

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Container index={1} photo={photos[1]} />
          <Container index={2} photo={photos[2]} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignSelf: "center" }}>
        <Container index={3} photo={photos[3]} />
        <Container index={4} photo={photos[4]} />
        <Container index={5} photo={photos[5]} />
      </div>
    </div>
  );
};

/* =====================================================
   STYLES
===================================================== */

const removeBtn = {
  position: "absolute",
  padding: 0,
  top: 6,
  right: 6,
  borderRadius: "50%",
  border: "none",
  background: "#fff",

  cursor: "pointer",
};

const profileBtn = {
  position: "absolute",
  height: "20%",
  width: "100%",
  left: "50%",
  bottom: 0,
  transform: "translateX(-50%)",

  fontSize: 10,
  borderRadius: "0px 0px 14px 14px",
  border: "1.5px solid rgba(230,0,0,0.81)",
  background: "#fff",
  color: "rgba(230,0,0,0.81)",
  fontWeight: 600,
  cursor: "pointer",
};

<style>
  {`
.spinner {
  width: 46px;
  height: 46px;
  border: 4px solid #eee;
  border-top: 4px solid rgba(230,0,0,0.81);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
`}
</style>;
